import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { getOperationalRetentionAmount } from '../lib/reconciliationSummary';
import { logError } from "../lib/logError";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface ProjectRetentionSummary {
  projectId: string;
  projectName: string;
  totalHeld: number;
  totalReceived: number;
  outstanding: number;
  recoveryPercent: number;
  reportCount: number;
  paidClaimCount: number;
  unpaidClaimCount: number;
}

export interface MonthlyRetentionTrend {
  label: string;       // "Jan 2025"
  month: number;
  year: number;
  held: number;        // retention amount this month
  received: number;    // retention received this month
  cumulativeHeld: number;
  cumulativeReceived: number;
  cumulativeOutstanding: number;
}

export interface RetentionAgingBucket {
  label: string;       // "0-3 months"
  range: string;       // e.g. "0-3"
  amount: number;
  count: number;       // number of claim reports in this bucket
  color: string;
}

/** Per-project monthly retention for project-level trend charts */
export interface ProjectMonthlyRetention {
  projectId: string;
  projectName: string;
  monthlyData: { label: string; month: number; year: number; held: number; received: number; cumulativeHeld: number; cumulativeReceived: number }[];
  totalHeld: number;
  totalReceived: number;
}

export interface RetentionDashboardData {
  perProjectRetention: ProjectRetentionSummary[];
  monthlyTrend: MonthlyRetentionTrend[];
  agingBuckets: RetentionAgingBucket[];
  projectMonthlyRetention: ProjectMonthlyRetention[];

  // Aggregates
  totalHeld: number;
  totalReceived: number;
  totalOutstanding: number;
  recoveryPercent: number;
  projectCount: number;

  // State
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const AGING_COLORS = {
  '0-3':  '#10b981', // green, recent
  '3-6':  '#f59e0b', // amber, moderate
  '6-12': '#f97316', // orange, aging
  '12+':  '#ef4444', // red, overdue
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Calculate months since a claim date
// ─────────────────────────────────────────────────────────────────────────────

function monthsSince(claimMonth: number, claimYear: number): number {
  const now = new Date();
  const currentMonth = now.getMonth() + 1; // 1-indexed
  const currentYear = now.getFullYear();
  return (currentYear - claimYear) * 12 + (currentMonth - claimMonth);
}

function getAgingBucket(months: number): string {
  if (months <= 3) return '0-3';
  if (months <= 6) return '3-6';
  if (months <= 12) return '6-12';
  return '12+';
}

function getAgingLabel(range: string): string {
  switch (range) {
    case '0-3': return '0-3 Months';
    case '3-6': return '3-6 Months';
    case '6-12': return '6-12 Months';
    case '12+': return '12+ Months';
    default: return range;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useRetentionStats(): RetentionDashboardData {
  const [perProjectRetention, setPerProjectRetention] = useState<ProjectRetentionSummary[]>([]);
  const [monthlyTrend, setMonthlyTrend] = useState<MonthlyRetentionTrend[]>([]);
  const [agingBuckets, setAgingBuckets] = useState<RetentionAgingBucket[]>([]);
  const [projectMonthlyRetention, setProjectMonthlyRetention] = useState<ProjectMonthlyRetention[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // 1. Fetch all active projects
      const { data: projects, error: projError } = await supabase
        .from('claims_projects')
        .select('id, job_location')
        .or('status.eq.active,status.is.null');

      if (projError) throw projError;
      if (!projects || projects.length === 0) {
        setPerProjectRetention([]);
        setMonthlyTrend([]);
        setAgingBuckets([]);
        setProjectMonthlyRetention([]);
        setLoading(false);
        return;
      }

      // 2. Fetch all retention data from claims_reports
      const { data: reports, error: reportsError } = await supabase
        .from('claims_reports')
        .select('project_id, claim_month, claim_year, retention_amount, retention_received_amount, reconciliation_summary')
        .in('project_id', projects.map(p => p.id));

      if (reportsError) throw reportsError;

      const allReports = (reports || []).map(r => ({
        projectId: r.project_id as string,
        claimMonth: r.claim_month as number,
        claimYear: r.claim_year as number,
        held: getOperationalRetentionAmount(r),
        received: Number(r.retention_received_amount) || 0,
      }));

      // Build project name map
      const nameMap = new Map<string, string>();
      for (const p of projects) {
        nameMap.set(p.id, p.job_location);
      }

      // ─── 3. Per-project aggregation ───
      const projectMap = new Map<string, { held: number; received: number; reportCount: number; paidCount: number; unpaidCount: number }>();

      for (const r of allReports) {
        const existing = projectMap.get(r.projectId);
        const isPaid = r.received > 0;
        const isUnpaid = r.held > 0 && r.received === 0;

        if (existing) {
          existing.held += r.held;
          existing.received += r.received;
          existing.reportCount += 1;
          if (isPaid) existing.paidCount += 1;
          if (isUnpaid) existing.unpaidCount += 1;
        } else {
          projectMap.set(r.projectId, {
            held: r.held,
            received: r.received,
            reportCount: 1,
            paidCount: isPaid ? 1 : 0,
            unpaidCount: isUnpaid ? 1 : 0,
          });
        }
      }

      const projectSummaries: ProjectRetentionSummary[] = [...projectMap.entries()]
        .filter(([, data]) => data.held > 0)
        .map(([projectId, data]) => ({
          projectId,
          projectName: nameMap.get(projectId) || 'Unknown',
          totalHeld: data.held,
          totalReceived: data.received,
          outstanding: data.held - data.received,
          recoveryPercent: data.held > 0 ? (data.received / data.held) * 100 : 0,
          reportCount: data.reportCount,
          paidClaimCount: data.paidCount,
          unpaidClaimCount: data.unpaidCount,
        }))
        .sort((a, b) => b.outstanding - a.outstanding);

      setPerProjectRetention(projectSummaries);

      // ─── 4. Monthly trend (aggregate across all projects) ───
      const monthMap = new Map<string, { month: number; year: number; held: number; received: number }>();

      for (const r of allReports) {
        if (r.held === 0 && r.received === 0) continue;
        const key = `${r.claimYear}-${String(r.claimMonth).padStart(2, '0')}`;
        const existing = monthMap.get(key);
        if (existing) {
          existing.held += r.held;
          existing.received += r.received;
        } else {
          monthMap.set(key, { month: r.claimMonth, year: r.claimYear, held: r.held, received: r.received });
        }
      }

      const sortedMonths = [...monthMap.entries()]
        .sort(([a], [b]) => a.localeCompare(b));

      let cumHeld = 0;
      let cumReceived = 0;
      const trend: MonthlyRetentionTrend[] = sortedMonths.map(([, data]) => {
        cumHeld += data.held;
        cumReceived += data.received;
        return {
          label: `${MONTH_NAMES[data.month - 1]} ${data.year}`,
          month: data.month,
          year: data.year,
          held: data.held,
          received: data.received,
          cumulativeHeld: cumHeld,
          cumulativeReceived: cumReceived,
          cumulativeOutstanding: cumHeld - cumReceived,
        };
      });

      setMonthlyTrend(trend);

      // ─── 5. Aging analysis ───
      const agingMap = new Map<string, { amount: number; count: number }>();

      for (const r of allReports) {
        const outstanding = r.held - r.received;
        if (outstanding <= 0) continue;

        const months = monthsSince(r.claimMonth, r.claimYear);
        const bucket = getAgingBucket(months);
        const existing = agingMap.get(bucket);
        if (existing) {
          existing.amount += outstanding;
          existing.count += 1;
        } else {
          agingMap.set(bucket, { amount: outstanding, count: 1 });
        }
      }

      const bucketOrder = ['0-3', '3-6', '6-12', '12+'];
      const aging: RetentionAgingBucket[] = bucketOrder.map(range => ({
        label: getAgingLabel(range),
        range,
        amount: agingMap.get(range)?.amount || 0,
        count: agingMap.get(range)?.count || 0,
        color: AGING_COLORS[range as keyof typeof AGING_COLORS],
      }));

      setAgingBuckets(aging);

      // ─── 6. Per-project monthly retention (for project trend charts) ───
      const projectMonthMap = new Map<string, Map<string, { month: number; year: number; held: number; received: number }>>();

      for (const r of allReports) {
        if (r.held === 0 && r.received === 0) continue;
        if (!projectMonthMap.has(r.projectId)) {
          projectMonthMap.set(r.projectId, new Map());
        }
        const pMap = projectMonthMap.get(r.projectId)!;
        const key = `${r.claimYear}-${String(r.claimMonth).padStart(2, '0')}`;
        const existing = pMap.get(key);
        if (existing) {
          existing.held += r.held;
          existing.received += r.received;
        } else {
          pMap.set(key, { month: r.claimMonth, year: r.claimYear, held: r.held, received: r.received });
        }
      }

      const projectMonthly: ProjectMonthlyRetention[] = [...projectMonthMap.entries()]
        .map(([projectId, pMap]) => {
          const sorted = [...pMap.entries()].sort(([a], [b]) => a.localeCompare(b));
          let pCumHeld = 0;
          let pCumReceived = 0;
          const monthlyData = sorted.map(([, data]) => {
            pCumHeld += data.held;
            pCumReceived += data.received;
            return {
              label: `${MONTH_NAMES[data.month - 1]} ${data.year}`,
              month: data.month,
              year: data.year,
              held: data.held,
              received: data.received,
              cumulativeHeld: pCumHeld,
              cumulativeReceived: pCumReceived,
            };
          });
          return {
            projectId,
            projectName: nameMap.get(projectId) || 'Unknown',
            monthlyData,
            totalHeld: pCumHeld,
            totalReceived: pCumReceived,
          };
        })
        .filter(p => p.totalHeld > 0)
        .sort((a, b) => b.totalHeld - a.totalHeld);

      setProjectMonthlyRetention(projectMonthly);

    } catch (err: unknown) {
      logError('Error fetching retention stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to load retention data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Computed aggregates
  const totalHeld = perProjectRetention.reduce((s, p) => s + p.totalHeld, 0);
  const totalReceived = perProjectRetention.reduce((s, p) => s + p.totalReceived, 0);
  const totalOutstanding = totalHeld - totalReceived;
  const recoveryPercent = totalHeld > 0 ? (totalReceived / totalHeld) * 100 : 0;
  const projectCount = perProjectRetention.length;

  return {
    perProjectRetention,
    monthlyTrend,
    agingBuckets,
    projectMonthlyRetention,
    totalHeld,
    totalReceived,
    totalOutstanding,
    recoveryPercent,
    projectCount,
    loading,
    error,
    refresh: fetchData,
  };
}
